/**
 * WebRTC client — Twilio Voice SDK wrapper.
 *
 * Tokens come from apex-voice — WS (cmd.token_refresh) when connected,
 * REST (/auth/voice-sdk-token proxy) as fallback when WS is unavailable.
 * Telnyx support has been removed — apex-voice is Twilio-only.
 */

import { request as voiceRequest, isConnected as isVoiceWsConnected } from './voice-ws';
import { apiFetch } from './api';
import { debugEmit } from './debug-stream';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type WebRtcCallState =
  | 'pending'
  | 'ringing'
  | 'connecting'
  | 'active'
  | 'held'
  | 'reconnecting'
  | 'disconnected';

export interface WebRtcCall {
  id: string;
  direction: 'inbound' | 'outbound';
  state: WebRtcCallState;
  from: string;
  to: string;
  callSid: string;
  accept(): void;
  reject(): void;
  hangup(): void;
  mute(): void;
  unmute(): void;
  isMuted(): boolean;
  // DTMF intentionally omitted — we route DTMF server-side via cmd.dtmf so
  // tones can be targeted at a specific conference participant. Browser-side
  // sendDigits would put DTMF into the conference mix where the contact
  // hears the beeps and external IVRs may not detect them reliably.
  onStateChange(cb: (state: WebRtcCallState) => void): void;
}

export interface WebRtcCallbacks {
  onRegistered?: () => void;
  onUnregistered?: () => void;
  onIncoming?: (call: WebRtcCall) => void;
  onError?: (error: Error) => void;
}

export interface WebRtcClient {
  register(): Promise<void>;
  unregister(): Promise<void>;
  destroy(): void;
  updateToken(token: string): void;
  connect(params: Record<string, string>): Promise<WebRtcCall>;
  checkMicPermission(): Promise<boolean>;
}

// ---------------------------------------------------------------------------
// Token fetch — WS primary, REST fallback
// ---------------------------------------------------------------------------

/** Fetch a Twilio Voice SDK token via REST (fallback when WS unavailable). */
async function fetchVoiceTokenRest(): Promise<{ token: string; identity: string }> {
  const resp = await apiFetch('/auth/voice-sdk-token');
  if (!resp.ok) throw new Error(`Voice SDK token REST failed: ${resp.status}`);
  const data = (await resp.json()) as { token: string; identity: string };
  return data;
}

/** Fetch a Twilio Voice SDK token — WS when connected, REST fallback. */
export async function fetchVoiceToken(): Promise<{ token: string; identity: string }> {
  if (isVoiceWsConnected()) {
    try {
      const result = await voiceRequest<{ token: string; identity: string; expires_at?: string }>(
        'cmd.token_refresh',
      );
      return { token: result.token, identity: result.identity };
    } catch (err) {
      debugEmit('webrtc', 'WS token refresh failed, trying REST', { error: String(err) }, 'warn');
    }
  }
  return fetchVoiceTokenRest();
}

// ---------------------------------------------------------------------------
// Twilio client factory
// ---------------------------------------------------------------------------

/** Create and initialize the Twilio Voice SDK WebRTC client. */
export async function createWebRtcClient(
  token: string,
  callbacks: WebRtcCallbacks,
): Promise<WebRtcClient> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { Device } = (await import('@twilio/voice-sdk')) as any;

  const device = new Device(token, {
    logLevel: 'warn',
    closeProtection: true,
    // Pinned edge — predictable RTT + Voice Insights segmentation. Most of
    // our reps and Twilio tenants are US East/Central; ashburn is the
    // shortest hop. Roaming default would let RTT vary call to call.
    edge: 'ashburn',
    // Auto-recover from transient signaling drops without ending the call.
    // Default is 0 (no reconnect; first signaling blip = call ended). 15s
    // covers most network blips while staying under the SDK's 30s ceiling.
    maxCallSignalingTimeoutMs: 15000,
  });

  // --- Event wiring ---

  device.on('registered', () => callbacks.onRegistered?.());
  device.on('unregistered', () => callbacks.onUnregistered?.());
  device.on('error', (err: Error) => callbacks.onError?.(err));

  device.on('tokenWillExpire', () => {
    void (async () => {
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const { token: newToken } = await fetchVoiceToken();
          device.updateToken(newToken);
          return;
        } catch {
          if (attempt < 2) await new Promise((r) => setTimeout(r, 1000 * 2 ** attempt));
        }
      }
      callbacks.onError?.(new Error('Failed to refresh voice token after 3 attempts'));
    })();
  });

  device.on('incoming', (twilioCall: TwilioCallLike) => {
    callbacks.onIncoming?.(wrapTwilioCall(twilioCall, 'inbound'));
  });

  // --- Client interface ---

  return {
    async register() {
      await device.register();
    },
    async unregister() {
      device.unregister();
    },
    destroy() {
      device.destroy();
    },
    updateToken(newToken: string) {
      device.updateToken(newToken);
    },
    async connect(params: Record<string, string>): Promise<WebRtcCall> {
      const call = await device.connect({ params });
      return wrapTwilioCall(call as TwilioCallLike, 'outbound');
    },
    async checkMicPermission(): Promise<boolean> {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        stream.getTracks().forEach((t) => t.stop());
        return true;
      } catch {
        return false;
      }
    },
  };
}

// ---------------------------------------------------------------------------
// SDK quality metric emit (browser → /api/voice/sdk-metric → Loki)
// ---------------------------------------------------------------------------

/**
 * Forward a Twilio Voice SDK quality datapoint to the apex-queue server,
 * which writes it through the messageLifecycle pipeline so it lands in
 * Loki under the call's CallSid. Fire-and-forget — a failed POST must
 * never affect call control.
 */
async function postSdkMetric(callSid: string, metric: Record<string, unknown>): Promise<void> {
  try {
    await apiFetch('/api/voice/sdk-metric', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ call_sid: callSid, metric }),
    });
  } catch {
    // Best-effort observability — swallow.
  }
}

// ---------------------------------------------------------------------------
// Twilio call wrapper
// ---------------------------------------------------------------------------

interface TwilioCallLike {
  parameters: Record<string, string>;
  status(): string;
  accept(): void;
  reject(): void;
  disconnect(): void;
  mute(m: boolean): void;
  isMuted(): boolean;
  on(event: string, handler: (...args: unknown[]) => void): void;
}

function wrapTwilioCall(call: TwilioCallLike, direction: 'inbound' | 'outbound'): WebRtcCall {
  let state: WebRtcCallState = direction === 'inbound' ? 'ringing' : 'pending';
  const stateCallbacks: ((s: WebRtcCallState) => void)[] = [];
  // Sample event fires every second; we forward at most one snapshot per
  // 5s window to keep Loki volume reasonable. Latest-wins.
  let lastSample: Record<string, unknown> | null = null;
  let sampleTimer: number | null = null;

  function setState(s: WebRtcCallState) {
    state = s;
    for (const cb of stateCallbacks) cb(s);
  }

  function emitMetric(metric: Record<string, unknown>) {
    const sid = call.parameters['CallSid'] ?? '';
    if (!sid) return;
    void postSdkMetric(sid, metric);
  }

  function startSampleTimer() {
    if (sampleTimer !== null) return;
    sampleTimer = window.setInterval(() => {
      if (lastSample) {
        emitMetric({ kind: 'sample', ...lastSample });
        lastSample = null;
      }
    }, 5000);
  }

  function stopSampleTimer() {
    if (sampleTimer !== null) {
      window.clearInterval(sampleTimer);
      sampleTimer = null;
    }
  }

  call.on('sample', (...args: unknown[]) => {
    const sample = args[0];
    if (sample && typeof sample === 'object') {
      lastSample = sample as Record<string, unknown>;
    }
  });
  call.on('warning', (...args: unknown[]) => {
    const [name, data] = args;
    emitMetric({
      kind: 'warning',
      name: typeof name === 'string' ? name : String(name),
      ...(data && typeof data === 'object' ? { data: data as Record<string, unknown> } : {}),
    });
  });
  call.on('warning-cleared', (...args: unknown[]) => {
    const [name] = args;
    emitMetric({
      kind: 'warning-cleared',
      name: typeof name === 'string' ? name : String(name),
    });
  });

  call.on('accept', () => {
    setState('active');
    startSampleTimer();
  });
  call.on('disconnect', () => {
    setState('disconnected');
    stopSampleTimer();
  });
  call.on('cancel', () => {
    setState('disconnected');
    stopSampleTimer();
  });
  call.on('reject', () => {
    setState('disconnected');
    stopSampleTimer();
  });
  call.on('ringing', () => {
    if (state === 'pending') setState('ringing');
  });
  // Signaling reconnect lifecycle. The SDK fires these when ICE/signaling
  // briefly drops and the maxCallSignalingTimeoutMs window kicks in.
  call.on('reconnecting', (...args: unknown[]) => {
    setState('reconnecting');
    const err = args[0];
    emitMetric({
      kind: 'reconnecting',
      ...(err ? { error: String(err) } : {}),
    });
  });
  call.on('reconnected', () => {
    setState('active');
    emitMetric({ kind: 'reconnected' });
  });

  return {
    get id() {
      return call.parameters['CallSid'] ?? '';
    },
    direction,
    get state() {
      return state;
    },
    from: call.parameters['From'] ?? '',
    to: call.parameters['To'] ?? '',
    get callSid() {
      return call.parameters['CallSid'] ?? '';
    },
    accept() {
      call.accept();
    },
    reject() {
      call.reject();
    },
    hangup() {
      call.disconnect();
    },
    mute() {
      call.mute(true);
    },
    unmute() {
      call.mute(false);
    },
    isMuted() {
      return call.isMuted();
    },
    onStateChange(cb: (s: WebRtcCallState) => void) {
      stateCallbacks.push(cb);
    },
  };
}
